/**
 * Canonieke rol-helpers — één bron van waarheid voor "eigenaar-niveau".
 *
 * Historisch checkte de helft van de app `role === 'owner'` en de andere helft
 * `role === 'EIGENAAR'`, terwijl de prod-DB de winkeleigenaar als `admin` opslaat
 * (JWT-rol = User.role; switch-entity zet géén UserEntityAccess-rol in het token).
 * Daardoor faalden owner-checks en owner-notificaties stil. Gebruik overal deze
 * helpers i.p.v. losse string-vergelijkingen.
 */

/** Rollen die als winkel-eigenaar/beheerder tellen in de JWT (User.role). */
export const OWNER_ROLES = ['superadmin', 'admin', 'owner', 'EIGENAAR'] as const;

/** Mag deze (JWT-)rol eigenaar-acties uitvoeren? Superadmin altijd. */
export function isOwnerRole(role?: string | null): boolean {
  return !!role && (OWNER_ROLES as readonly string[]).includes(role);
}

/** Rol-set voor Prisma-queries op User.role om de winkel-eigenaar(s) te vinden. */
export const OWNER_USER_ROLES = ['admin', 'owner', 'EIGENAAR'];

/** Rol-set voor Prisma-queries op UserEntityAccess.role. */
export const OWNER_ACCESS_ROLES = ['owner', 'EIGENAAR', 'admin'];

/**
 * Rollen die via een uitnodiging toegekend mogen worden. Nooit superadmin/owner/admin:
 * die rechten geeft een beheerder expliciet in Instellingen → Gebruikers.
 * Onboarding-wizard gebruikt eigen labels (VERKOPER, BACKOFFICE, PLAATSER, OPMETER) → vertaald.
 */
const UITNODIGING_ROLLEN = ['binnendienst', 'buitendienst', 'chauffeur', 'manager'] as const;
const WIZARD_ROL_MAP: Record<string, string> = {
  VERKOPER: 'binnendienst', BACKOFFICE: 'binnendienst', PLAATSER: 'buitendienst', OPMETER: 'buitendienst',
};

/** Vertaalt een gevraagde rol naar een toegelaten uitnodigingsrol; onbekend of te hoog → binnendienst. */
export function uitnodigingsRol(gevraagd?: string | null): string {
  if (!gevraagd) return 'binnendienst';
  const r = WIZARD_ROL_MAP[gevraagd] ?? gevraagd;
  return (UITNODIGING_ROLLEN as readonly string[]).includes(r) ? r : 'binnendienst';
}
